import { useEffect } from "react";
import { MANUAL_PAYMENT_TYPE_ID } from "../../../../utils/manualPayment";
import { formatCurrency } from "../../../../utils/formatCurrency";
import {
  applyWalkInFinalPrice,
  walkInGrossFromTransit,
  walkInItemWeightRates,
  walkInSuggestedGross,
} from "../../../../utils/adminWalkInBooking";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-[3px] text-[13px] leading-5">
    <span className="text-stone-500 shrink-0">{label}</span>
    <span className="font-semibold text-stone-900 text-right break-all min-w-0">
      {value || "—"}
    </span>
  </div>
);

const paymentLabel = (id) => {
  if (String(id) === MANUAL_PAYMENT_TYPE_ID) return "Bank transfer";
  if (String(id) === "2") return "Online (Stripe)";
  if (String(id) === "1") return "Cash";
  return id ? `Method #${id}` : "—";
};

const WalkInSummary = ({
  data,
  fetchedFormData,
  transitCost,
  selectedCurrency,
  totalCost,
  vatAmountPercentage,
  updateFields,
  variant = "page",
}) => {
  const routeName =
    fetchedFormData?.Route?.find((r) => String(r.id) === String(data.route))
      ?.routeName || "";
  const countryName = (id) =>
    fetchedFormData?.country?.find((c) => String(c.id) === String(id))?.name ||
    "";
  const cityName = (id) =>
    fetchedFormData?.city?.find((c) => String(c.id) === String(id))?.name || "";

  const products = (
    Array.isArray(data.producDetails) ? data.producDetails : []
  ).filter(
    (item) =>
      item?.itemDescription ||
      item?.productTypeName ||
      item?.productGroupName ||
      Number(item?.productQty) > 0 ||
      Number(item?.productValue) > 0 ||
      Number(item?.itemWeight) > 0,
  );

  const calculatedGross = walkInGrossFromTransit(
    transitCost,
    totalCost,
    vatAmountPercentage,
  );
  const calculatedNet = Array.isArray(transitCost)
    ? transitCost.reduce(
        (sum, tc) =>
          sum +
          (parseFloat(tc?.totalCostWithoutVAT ?? tc?.totalCost ?? 0) || 0),
        0,
      )
    : parseFloat(totalCost || 0);
  const calculatedVat = Array.isArray(transitCost)
    ? transitCost.reduce(
        (sum, tc) =>
          sum +
          (parseFloat(tc?.vatPercentageAmount ?? tc?.vatAmount ?? 0) || 0),
        0,
      )
    : parseFloat(vatAmountPercentage || 0);

  const weightRates = walkInItemWeightRates({
    products: products.length ? products : data.producDetails,
    fallbackKg: data.productWeight,
    routeId: data.route,
    weights: fetchedFormData?.Weight || [],
    transitCost,
  }).filter((row) => row.kg > 0 || row.perKg != null);

  const suggestedGross = walkInSuggestedGross({
    calculatedGross,
    weightRates,
  });
  const suggestedNet =
    calculatedGross > 0 ? calculatedNet : suggestedGross;
  const suggestedVat = calculatedGross > 0 ? calculatedVat : 0;

  const payable = applyWalkInFinalPrice(
    suggestedGross,
    suggestedNet,
    suggestedVat,
    data.walkInFinalPrice,
  );

  const customPrice = parseFloat(data.walkInFinalPrice);
  const hasCustomPrice =
    Boolean(data.walkInFinalPriceEdited) &&
    Number.isFinite(customPrice) &&
    customPrice > 0 &&
    Math.abs(customPrice - suggestedGross) >= 0.005;

  useEffect(() => {
    if (typeof updateFields !== "function") return;
    if (hasCustomPrice) return;
    if (!(suggestedGross > 0)) return;
    const current = parseFloat(data.walkInFinalPrice);
    if (Number.isFinite(current) && Math.abs(current - suggestedGross) < 0.005) {
      return;
    }
    updateFields({
      walkInFinalPrice: suggestedGross.toFixed(2),
      walkInFinalPriceEdited: false,
    });
  }, [
    suggestedGross,
    hasCustomPrice,
    data.walkInFinalPrice,
    updateFields,
  ]);

  const createFor = data.walkInCreateAccountsFor || "None";
  const accountLine = (party) => {
    const prefix = party === "Sender" ? "walkInSender" : "walkInReceiver";
    const enabled = createFor === "Both" || createFor === party;
    if (!enabled) return "Not created";
    const mode = data[`${prefix}Mode`] || "None";
    if (mode === "Existing") {
      return `Existing · ${
        data[`${prefix}ExistingLabel`] ||
        data[`${prefix}ExistingUserId`] ||
        "selected"
      }`;
    }
    if (mode === "New") {
      return `New · ${data[`${prefix}UserName`] || "pending"}`;
    }
    return "None";
  };

  const senderPlace = [
    cityName(data.senderCity),
    countryName(data.senderCountry),
  ]
    .filter(Boolean)
    .join(", ");
  const receiverPlace = [
    cityName(data.receiverCity),
    countryName(data.receiverCountry),
  ]
    .filter(Boolean)
    .join(", ");

  const currency = selectedCurrency || "GBP";
  const amountLabel = formatCurrency(payable.gross, currency);

  const setFinalPrice = (value, edited) => {
    if (typeof updateFields !== "function") return;
    updateFields({
      walkInFinalPrice: value,
      walkInFinalPriceEdited: edited,
    });
  };

  const pricingBlock = (compact) => (
    <section className={compact ? "" : "rounded-lg border border-stone-200 bg-white p-3 mt-3"}>
      {compact ? <h3>Pricing</h3> : (
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-1.5">
          Pricing
        </h3>
      )}
      {weightRates.length === 0 ? (
        <p className="text-[13px] text-stone-500">Enter parcel weight to see the per kg rate.</p>
      ) : (
        weightRates.map((row) => (
          <div key={row.index} className={compact ? "mb-1" : "mb-2 last:mb-0"}>
            {weightRates.length > 1 ? (
              <p className="text-[11px] font-semibold text-stone-500 truncate">
                {row.label}
              </p>
            ) : null}
            <Row
              label="Per kg"
              value={
                row.perKg != null
                  ? `${formatCurrency(row.perKg, currency)}/kg`
                  : "—"
              }
            />
            <Row
              label="Weight"
              value={row.kg > 0 ? `${row.kg} kg` : "—"}
            />
            <Row
              label="Weight charge"
              value={
                row.charge != null
                  ? formatCurrency(row.charge, currency)
                  : "—"
              }
            />
          </div>
        ))
      )}
      <Row
        label="Calculated"
        value={suggestedGross > 0 ? formatCurrency(suggestedGross, currency) : "—"}
      />
      <div className="mt-2">
        <label className="flex items-center justify-between gap-2 text-[13px] text-stone-500">
          <span>Final price</span>
          {hasCustomPrice ? (
            <button
              type="button"
              className="text-[11px] font-bold text-orange-600 hover:underline"
              onClick={() =>
                setFinalPrice(
                  suggestedGross > 0 ? suggestedGross.toFixed(2) : "",
                  false,
                )
              }
            >
              Reset
            </button>
          ) : null}
        </label>
        <div className="mt-1 flex items-center gap-2">
          <input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            className="w-full h-9 rounded-lg border border-stone-300 bg-white px-2.5 text-sm font-semibold text-stone-900 tabular-nums"
            value={
              hasCustomPrice
                ? data.walkInFinalPrice
                : suggestedGross > 0
                  ? suggestedGross.toFixed(2)
                  : data.walkInFinalPrice || ""
            }
            placeholder="0.00"
            onChange={(e) => {
              const next = e.target.value;
              if (next === "") {
                setFinalPrice("", false);
                return;
              }
              setFinalPrice(next, true);
            }}
            onBlur={() => {
              if (hasCustomPrice) return;
              if (suggestedGross > 0) {
                setFinalPrice(suggestedGross.toFixed(2), false);
              }
            }}
          />
        </div>
        <p className="text-[11px] text-stone-500 mt-1">
          {hasCustomPrice
            ? "This amount is sent as the payable total."
            : "Filled from the calculated total. Edit if you need a different amount."}
        </p>
      </div>
    </section>
  );

  const personBlock = (title, name, mobile, email, place, postCode) => (
    <div className="rounded-lg border border-stone-200 bg-white p-3">
      <h3 className="text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-1.5">
        {title}
      </h3>
      <p className="font-semibold text-stone-900">{name || "—"}</p>
      <p className="text-sm text-stone-600 mt-0.5">{mobile || "—"}</p>
      {email ? <p className="text-sm text-stone-600">{email}</p> : null}
      <p className="text-sm text-stone-600 mt-1">
        {[place, postCode].filter(Boolean).join(" · ") || "—"}
      </p>
    </div>
  );

  if (variant === "sidebar") {
    return (
      <aside className="walkin-order-summary">
        <div className="walkin-order-summary__head">
          <p className="walkin-order-summary__kicker">Order summary</p>
          <p className="walkin-order-summary__amount">
            {payable.gross > 0 ? amountLabel : "—"}
          </p>
          <p className="walkin-order-summary__meta">
            {paymentLabel(data.paymentTypeId)}
            {routeName ? ` · ${routeName}` : ""}
            {payable.overridden ? " · edited" : ""}
          </p>
        </div>

        <div className="walkin-order-summary__body">
          {pricingBlock(true)}

          <section>
            <h3>Route</h3>
            <Row label="Route" value={routeName} />
            <Row label="Payment" value={paymentLabel(data.paymentTypeId)} />
            <Row label="Owner" value={data.walkInBookingOwner || "Sender"} />
          </section>

          <section>
            <h3>Sender</h3>
            <Row label="Name" value={data.senderName} />
            <Row label="Mobile" value={data.senderMobileNumber} />
            {data.senderEmail ? (
              <Row label="Email" value={data.senderEmail} />
            ) : null}
            <Row label="Place" value={senderPlace} />
            <Row label="Post code" value={data.senderPostCode} />
          </section>

          <section>
            <h3>Receiver</h3>
            <Row label="Name" value={data.receiverName} />
            <Row label="Mobile" value={data.receiverMobileNumber} />
            {data.receiverEmail ? (
              <Row label="Email" value={data.receiverEmail} />
            ) : null}
            <Row label="Place" value={receiverPlace} />
            <Row label="Post code" value={data.receiverPostCode} />
          </section>

          <section>
            <h3>Products</h3>
            {products.length === 0 ? (
              <p className="text-[13px] text-stone-500">No items yet</p>
            ) : (
              <ul className="space-y-2">
                {products.map((item, idx) => (
                  <li
                    key={idx}
                    className="flex justify-between gap-2 text-[13px]"
                  >
                    <span className="min-w-0">
                      <span className="font-semibold text-stone-900 block truncate">
                        {item.itemDescription ||
                          item.productTypeName ||
                          item.productGroupName ||
                          `Item ${idx + 1}`}
                      </span>
                      <span className="text-xs text-stone-500">
                        Qty {item.productQty || 1}
                        {item.itemWeight ? ` · ${item.itemWeight}kg` : ""}
                        {item.deliveryPlanName
                          ? ` · ${item.deliveryPlanName}`
                          : ""}
                      </span>
                    </span>
                    <span className="font-semibold text-stone-900 shrink-0">
                      {formatCurrency(item.productValue, currency)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {createFor !== "None" ? (
            <section>
              <h3>Accounts</h3>
              <Row label="Create for" value={createFor} />
              <Row label="Sender" value={accountLine("Sender")} />
              <Row label="Receiver" value={accountLine("Receiver")} />
            </section>
          ) : null}
        </div>
      </aside>
    );
  }

  return (
    <div className="walkin-confirm w-full">
      <h2 className="text-base font-semibold text-stone-900">Confirm booking</h2>
      <p className="text-sm text-stone-500 mt-1">
        Check the details below, then create this walk-in booking.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
        {personBlock(
          "Sender",
          data.senderName,
          data.senderMobileNumber,
          data.senderEmail,
          senderPlace,
          data.senderPostCode,
        )}
        {personBlock(
          "Receiver",
          data.receiverName,
          data.receiverMobileNumber,
          data.receiverEmail,
          receiverPlace,
          data.receiverPostCode,
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
        <div className="rounded-lg border border-stone-200 bg-white p-3">
          <h3 className="text-[11px] font-bold uppercase tracking-wide text-stone-500">
            Route
          </h3>
          <p className="font-semibold text-stone-900 mt-1">
            {routeName || "—"}
          </p>
        </div>
        <div className="rounded-lg border border-stone-200 bg-white p-3">
          <h3 className="text-[11px] font-bold uppercase tracking-wide text-stone-500">
            Payment
          </h3>
          <p className="font-semibold text-stone-900 mt-1">
            {paymentLabel(data.paymentTypeId)}
          </p>
        </div>
        <div className="rounded-lg border border-stone-200 bg-white p-3">
          <h3 className="text-[11px] font-bold uppercase tracking-wide text-stone-500">
            Payable
          </h3>
          <p className="font-semibold text-stone-900 mt-1">
            {payable.gross > 0 ? amountLabel : "—"}
          </p>
        </div>
      </div>

      {pricingBlock(false)}

      <div className="rounded-lg border border-stone-200 bg-white p-3 mt-3">
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-2">
          Products
        </h3>
        {products.length === 0 ? (
          <p className="text-sm text-stone-500">No items yet</p>
        ) : (
          <ul className="divide-y divide-stone-100">
            {products.map((item, idx) => (
              <li
                key={idx}
                className="flex justify-between gap-3 py-2 first:pt-0 last:pb-0 text-sm"
              >
                <span className="min-w-0">
                  <span className="font-semibold text-stone-900 block">
                    {item.itemDescription ||
                      item.productTypeName ||
                      item.productGroupName ||
                      `Item ${idx + 1}`}
                  </span>
                  <span className="text-xs text-stone-500">
                    Qty {item.productQty || 1}
                    {item.itemWeight ? ` · ${item.itemWeight}kg` : ""}
                  </span>
                </span>
                <span className="font-semibold text-stone-900 shrink-0">
                  {formatCurrency(item.productValue, currency)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {createFor !== "None" ? (
        <div className="rounded-lg border border-stone-200 bg-white p-3 mt-3">
          <h3 className="text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-1.5">
            Accounts
          </h3>
          <Row label="Create for" value={createFor} />
          <Row label="Sender" value={accountLine("Sender")} />
          <Row label="Receiver" value={accountLine("Receiver")} />
        </div>
      ) : null}
    </div>
  );
};

export default WalkInSummary;
